import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { apiMessage, setupApi } from '../spacesLib';
import type { SpaceKind } from './SpaceDialog';

export interface SpaceDeleteTarget {
  kind: SpaceKind;
  id: string;
  /** What the operator called it — the name they are about to lose. */
  label: string;
}

/**
 * The confirmation in front of `DELETE /floors|/areas|/tables`.
 *
 * Its whole value is the sentence it shows, and that sentence differs by rung:
 * a floor or an area must be emptied first and the API says so, while a table
 * goes immediately and takes its QR code with it, detaching sessions,
 * reservations and service requests (`onDelete: SetNull`) rather than refusing.
 * Two screens now offer this delete — the Spaces tab and the setup wizard — and
 * a second copy of that warning is how one of them quietly starts promising the
 * wrong thing.
 *
 * The request lives here with the warning for the same reason: the API's own
 * refusal ("this floor still has 3 areas") is more use than a generic failure,
 * and it would only be surfaced by whichever copy remembered to.
 */
export function SpaceDeleteDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: SpaceDeleteTarget | null;
  onClose: () => void;
  /** Ran after the server confirmed it. Reload the rung; do not assume it. */
  onDeleted: (target: SpaceDeleteTarget) => void;
}) {
  const { t } = useTranslation();

  const remove = async () => {
    if (!target) return;
    const client =
      setupApi[target.kind === 'floor' ? 'floors' : target.kind === 'area' ? 'areas' : 'tables'];
    try {
      await client.remove(target.id);
      toast.success(t('setup.deleted'));
      onDeleted(target);
    } catch (e) {
      // The API refuses to delete a floor that still has areas, and an area that
      // still has tables — surface that rather than a generic failure.
      toast.error(apiMessage(e, t('setup.deleteFailed')));
    } finally {
      onClose();
    }
  };

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={t('setup.confirmDelete')}
      description={target?.label}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={() => void remove()}>
            {t('common.delete')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-muted">
        {target?.kind === 'table' ? t('setup.table.deleteWarning') : t('setup.deleteWarning')}
      </p>
    </Dialog>
  );
}
